<template>
  <v-row class="match-height">
    <v-col cols="12">
      <v-card>
        <v-card-title>{{ $t("Keskustelukanavat") }}</v-card-title>
        <v-card-text>
          <v-form v-on:submit.prevent="submit" ref="form">

            <v-card outlined elevation="0" class="mb-4">
              <v-tabs v-model="tab" grow>
                <v-tab v-for="lang in tenant.languages" :key="'langtab-' + lang.code">
                  <img :src="lang.icon" style="max-height: 20px" />
                  &nbsp;&nbsp;{{ lang.name }}
                  &nbsp;
                  <v-icon v-if="langOk(channel.translations, lang.code, ['name'])" color="success">
                    {{ icons.mdiCheck }}
                  </v-icon>
                  <v-icon v-else color="error">
                    {{ icons.mdiBlockHelper }}
                  </v-icon>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab">
                <v-tab-item v-for="lang in tenant.languages" :key="'langtabitem-' + lang.code">
                  <div style="padding: 20px 20px 0 20px">
                    <img :src="lang.icon" style="max-height: 14px" />
                    <br />
                    <br />

                    <v-text-field :id="'channel-title-' + lang.code" v-model="channel.translations[lang.code].name"
                      :label="$t('Nimi')" outlined class="mb-4" hide-details="auto" />
                    <div style="height: 240px">
                      <v-textarea counter outlined :rules="[(v) => v.length <= 500 || 'Max 500 merkkiä']"
                        :label="$t('Kuvaus')" :id="'channel-text-' + lang.code"
                        v-model="channel.translations[lang.code].description" height="200" />
                    </div>
                  </div>
                </v-tab-item>
              </v-tabs-items>

              <v-input class="ml-4 mb-4" hidden hide-details="auto" :rules="[validateLangs]"></v-input>
            </v-card>

            <div class="form-group">
              <label for="image">{{ $t("Kuva") }}</label>
              <br />
              <small class="form-text text-muted">{{
                $t(
                  "Raahaa kuva tähän tai klikkaa tästä ja valitse kuva tiedostoista."
                )
              }}</small>
              <br />
              <croppa :prevent-white-space="true" v-model="myCroppa" :width="300" :height="300" accept=".jpeg,.jpg,.png"
                @file-type-mismatch="onFileTypeMismatch" :placeholder-font-size="12" :initial-image="channel.image"
                @image-remove="removeImage" @new-image-drawn="imageLoaded"></croppa>
            </div>

            <div class="mb-4">
              <v-radio-group v-model="channel.group" :label="$t('Ryhmä')" hide-details="auto" :rules="inputRules.group">
                <v-radio v-for="group in user.admin_groups" :key="group.id" :value="group.id" :label="group.name" />
              </v-radio-group>
            </div>
            <div class="mb-4">
              <v-radio-group v-model="channel.channel_type" :label="$t('Kanavan tyyppi')" hide-details="auto"
                :rules="[requiredRadio]">
                <v-radio :label="$t('Yksityinen keskustelu')" :value="1"></v-radio>
                <v-radio :label="$t('Ryhmäkeskustelu')" :value="2"></v-radio>
              </v-radio-group>
            </div>
            <div class="mb-4" v-if="channel.channel_type == 2">
              <v-switch v-model="channel.send_notifications" :label="$t('Push-ilmoitukset käytössä')">
              </v-switch>
            </div>
            <div class="mt-6 mb-4">
              <v-autocomplete v-model="channel.managers" :label="$t('Kanavaa hallinnoivat')" :items="users"
                :filter="filterUsers" item-value="id" chips clearable multiple cache-items>
                <template v-slot:item="data">
                  {{ data.item.last_name }} {{ data.item.first_name }}
                </template>
                <template v-slot:selection="data">
                  <v-chip v-bind="data.attrs" :input-value="data.selected" close
                    @click:close="removeManager(data.item)">
                    {{ data.item.last_name }} {{ data.item.first_name }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </div>
            <div class="ma-4">
              <v-switch v-model="channel.is_active" :label="$t('Käytössä')" class="mb-5" persistent-hint :hint="channel.is_active
                ? $t('Kanava on käytössä')
                : $t('Kanava ei ole käytössä')
                "></v-switch>
            </div>
            <v-alert type="error" color="error" text v-if="!formIsValid">
              {{ $t("Tarkista lomakkeen tiedot") }}
            </v-alert>
            <v-btn color="primary" type="submit" :loading="isSubmitting">
              {{ $t("Tallenna") }}
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { onMounted, reactive, ref } from "@vue/composition-api";
import { useTranslations } from "@/@core/libs/i18n";
import useAuth from "@/useAuth";
import { required, requiredRadio } from "@/@core/utils/validation";
import { mdiCheck, mdiBlockHelper } from "@mdi/js";
import adminApi from "@/api/admin";
import { useRouter } from "@/@core/utils";
import {
  getValidTranslations,
  langOk,
} from "@/@core/utils/translation_helpers";
import "vue-croppa/dist/vue-croppa.css";
import { i18n } from "@/plugins/i18n";
import ChatChannelListItem from "@/views/chat/compontents/chatChannelListItem.vue";

export default {
  props: ["id"],
  setup(props) {
    const { router } = useRouter();
    const { tenant, user } = useAuth();

    const tab = ref(null);
    const form = ref(null);
    const formIsValid = ref(true);
    const formErrors = ref([]);
    const isSubmitting = ref(false);

    const channel = reactive({
      id: null,
      translations: {},
      is_active: true,
      managers: [],
      channel_type: null,
      image: "",
      group: null,
      send_notifications: null
    });

    const search = ref(null);
    const select = ref(null);
    const myCroppa = ref({});
    const imageRemoved = ref(false);
    const users = ref([]);

    tenant.value.languages.forEach((lang) => {
      channel.translations[lang.code] = {
        name: "",
        description: "",
      };
    });

    const fetchUsers = async (id) => {
      const response = await adminApi.getUsers(1, 1000);
      if (response.ok) {
        users.value = response.data.results;
      } else {
        alert(response.error);
      }
    };

    const fetchChannel = async (id) => {
      const response = await adminApi.getChannel(id);
      if (response.ok) {
        channel.id = response.data.id;
        channel.is_active = response.data.is_active;
        channel.managers = response.data.managers;
        channel.channel_type = response.data.channel_type;
        channel.send_notifications = response.data.send_notifications;
        if (response.data.group) {
          if (user.value.admin_groups.some(a => a.id == response.data.group))
            channel.group = response.data.group;
        }
        else {
          channel.group = getDefaultGroup();
        }

        channel.translations = {};
        tenant.value.languages.forEach((lang) => {
          if (response.data.translations[lang.code]) {
            channel.translations[lang.code] =
              response.data.translations[lang.code];
          } else {
            channel.translations[lang.code] = {
              name: "",
              description: "",
            };
          }
        });

        if (response.data.image) {
          const fullSize = response.data.image["full_size"];
          if (fullSize) {
            channel.image = fullSize;
          }

          myCroppa.value.refresh();
        }
      } else {
        alert(response.error);
      }
    };

    const submit = async () => {
      formIsValid.value = true;
      if (form.value.validate()) {
        isSubmitting.value = true;
        const saveChannelsRequest = Object.assign({}, channel);

        // Translations
        saveChannelsRequest.translations = getValidTranslations(
          channel.translations,
          ["name"]
        );

        // Image
        let newImageName = "";
        let imageBlob = null;
        let removeImage = false;
        if (myCroppa.value.hasImage()) {
          if (myCroppa.value.chosenFile) {
            imageBlob = await myCroppa.value.promisedBlob(
              myCroppa.value.chosenFile.type,
              0.8
            );
            newImageName = myCroppa.value.chosenFile.name;
          }
        } else if (imageRemoved.value) {
          removeImage = true;
        }

        const response = await adminApi.saveChannel(
          saveChannelsRequest,
          imageBlob,
          newImageName,
          removeImage
        );

        if (response.ok) {
          router.push({ name: "admin-chat-channel-list" });
        } else {
          alert(response.error);
        }
        isSubmitting.value = false;
      } else {
        formIsValid.value = false;
      }
    };

    const removeChannel = async () => {
      // alert("TODO");
    };

    const removeManager = async (manager) => {
      channel.managers.splice(channel.managers.findIndex(a => a == manager.id), 1);
    }

    const validateLangs = () => {
      let valid = false;
      tenant.value.languages.forEach((lang) => {
        if (langOk(channel.translations, lang.code, ["name"])) valid = true;
      });

      if (valid) {
        return true;
      } else {
        return i18n.t("Nimi puuttuu");
      }
    };

    const onFileTypeMismatch = (file) => {
      alert(
        i18n.t(
          "Tiedostomuoto ei ole tuettu. Tuettuja tiedostomuotoja ovat jpeg sekä png."
        )
      );
    };

    onMounted(() => {
      fetchUsers();
      if (props.id) {
        fetchChannel(props.id);
      }
    });

    const imageLoaded = () => {
      imageRemoved.value = false;
    };

    const removeImage = () => {
      channel.image = null;
      imageRemoved.value = true;
    };

    const filterUsers = (item, queryText, itemText) => {
      return (
        item.is_active && (
          item.first_name
            .toLocaleLowerCase()
            .indexOf(queryText.toLocaleLowerCase()) > -1 ||
          item.last_name
            .toLocaleLowerCase()
            .indexOf(queryText.toLocaleLowerCase()) > -1
        ));
    };

    const getDefaultGroup = () => {
      return user.value.admin_groups.find(a => a.group_type == 1).id;
    }

    const inputRules = {
      group: [(v) => !!v || i18n.t("Ryhmä on pakollinen")],
    }

    return {
      inputRules,
      channel,
      tenant,
      users,
      filterUsers,
      myCroppa,
      tab,
      formErrors,
      user,
      submit,
      form,
      onFileTypeMismatch,
      required,
      requiredRadio,
      langOk,
      validateLangs,
      removeManager,
      removeImage,
      imageLoaded,
      formIsValid,
      isSubmitting,
      removeChannel,
      icons: {
        mdiCheck,
        mdiBlockHelper,
      },
    };
  },
};
</script>
